import { gql } from '@apollo/client';
import { Button, Col, DatePicker, Form, Row, Select, Slider } from 'antd';
import { useQuery } from 'hooks/apollo';
import React, { useEffect, useMemo } from 'react';
import {
  PLANS,
} from 'shared/constants/urls';
import { FormattedMessage } from 'react-intl';
import { currencies, SEK } from 'shared/constants/currencies';
import { Module, LicenseInterval, DataSourceType } from 'shared/prisma/types';

import globalMessages from 'translations/messages';
import useUser from 'hooks/useUser';
import CurrencySelect from 'components/CurrencySelect';
import DashboardCard from 'components/DashboardCard';
import { ArrowDownOutlined, ArrowUpOutlined, CheckCircleOutlined, DeleteFilled, ExportOutlined, PlusOutlined } from '@ant-design/icons';
import { MODULE_LIMIT_SETTING, MODULE_LIMIT_SETTING_LABEL } from 'shared/constants/settings';
import { currencyFormat } from 'shared/utils/formatter';
import { dayjs } from 'shared/utils/date';
import useFormatMessage from 'hooks/useFormatMessage';
import { LICENSE_LEVELS, dataSourceModules } from 'shared/constants/license';
import { formatLicenseLimit, getModuleLimitSetting, getValidLicenseInterval, isLimitInPriceOptions } from 'shared/utils/license';
import useSettings from 'hooks/useSettings';

const moduleDescriptions = {
  [Module.Analytics]: 'Intuitive analytics, yet as powerful as you need it to be. Measure traffic, behaviour, marketing, and conversions without intruding on your visitors\' privacy.',
  [Module.UxSurveys]: 'Understand who your users are, their intents, issues, and experiences. Get feedback on specific pages or functionality. High response rates and compliant around the world.',
  [Module.Heatmaps]: 'Make your visitors come to life and see things from their point of view. Optimise mobile and desktop experience and smoothen user journeys for higher conversion.',
  [Module.Accessibility]: 'Collect, assess, and retain evidence about website accessibility against a WCAG baseline.',
};

const dataSourceIntervalOptions = {
  [DataSourceType.Website]: [LicenseInterval.Yearly, LicenseInterval.Monthly],
  [DataSourceType.Cx]: [LicenseInterval.Yearly],
};

const licenseLevelOptions = [
  LICENSE_LEVELS.BUSINESS,
  LICENSE_LEVELS.ENTERPRISE,
];

const LICENSES = gql`
  query ActiveModuleLicenses($levels: [Int!], $modules: [Module!]) {
    activeModuleLicenses(levels: $levels, modules: $modules) {
      id
      title
      module
      level
      settings(keys: ["prices"])
    }
  }
`;

const getModuleLicense = ({ activeModuleLicenses, level, module }) => activeModuleLicenses?.find(l => l.level === level && l.module === module);

const compareModuleLimits = (currentLimit, selectedLimit) => {
  const current = Number(currentLimit);
  const selected = Number(selectedLimit);

  if (!Number.isFinite(current) || !Number.isFinite(selected)) {
    return 0;
  }

  if (selected > current) {
    return 1;
  }

  if (selected < current) {
    return -1;
  }

  return 0;
};

const ModuleLicenseCard = ({ module, index, activeModuleLicenses, value, onChange, activeLicense, loading, isMidTermChange, isSuperUser }) => {
  const currency = Form.useWatch('currency');
  const level = Form.useWatch('level');
  const interval = Form.useWatch('interval');
  const limitSetting = MODULE_LIMIT_SETTING[module];
  const license = getModuleLicense({ activeModuleLicenses, level, module });
  const prices = license?.settings.prices;
  const priceData = prices?.[value?.priceLevel];

  const actions = [
    <a href={PLANS} target="_blank">
      <Button type="link" icon={<ExportOutlined />}>View all features</Button>
    </a>,
  ];

  const isTrial = activeLicense?.interval === LicenseInterval.Trial;
  const activeLicenseModule = activeLicense?.modules.find(m => m.module === module);
  const activeLicenseLimit = activeLicense?.settings[limitSetting];

  const limit = priceData?.[limitSetting];
  let monthlyPrice = priceData?.monthlyPrice[currency];

  if (interval === LicenseInterval.Yearly && monthlyPrice) {
    monthlyPrice *= 0.8;
  }

  if (license) {
    if (value?.added) {
      if (activeLicenseModule && !isTrial) {
        const limitComparison = compareModuleLimits(activeLicenseLimit, priceData?.[limitSetting]);

        if (isLimitInPriceOptions(license, limitSetting, activeLicenseLimit)) {
          if (limitComparison > 0) {
            actions.push(<Button disabled icon={<ArrowUpOutlined />} type="link">Upgrade</Button>);
          } else if (limitComparison < 0) {
            actions.push(<Button disabled icon={<ArrowDownOutlined />} type="link">Downgrade</Button>);
          } else {
            actions.push(<Button disabled icon={<CheckCircleOutlined />} type="link">Current</Button>);
          }
        }
      } else {
        actions.push(<Button icon={<DeleteFilled />} onClick={() => onChange({ ...value, added: false })} type="link" danger>Remove</Button>);
      }
    } else {
      actions.push(<Button icon={<PlusOutlined />} onClick={() => onChange({ ...value, added: true })} type="link">Add</Button>);
    }
  }

  useEffect(() => {
    if (!limit && prices?.length > 0) {
      onChange({ ...value, priceLevel: 0 });
    }
  }, [limit, prices]);

  const equalOrAboveCurrentValue = async (rule, v) => {
    const limitValue = prices?.[v]?.[limitSetting];
    if (!limitValue) {
      throw new Error('Invalid limit value');
    }
    if (isMidTermChange && !isSuperUser && activeLicenseLimit && Number(limitValue) < Number(activeLicenseLimit)) {
      throw new Error('Value must be equal or above current limit');
    }
  };

  return (
    <DashboardCard title={<FormattedMessage {...globalMessages[module]} />} module={module} actions={actions} loading={loading}>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <p style={{ flexGrow: 1 }}>{moduleDescriptions[module]}</p>
        <Form.Item
          name={['modules', index, 'priceLevel']}
          label={`${formatLicenseLimit(limit)} ${MODULE_LIMIT_SETTING_LABEL[limitSetting]}`}
          rules={[{ validator: equalOrAboveCurrentValue }]}
          dependencies={['startDate']}
          style={{ marginBottom: 0 }}
        >
          <Slider min={0} max={(prices?.length || 1) - 1} tooltip={{ open: false }} />
        </Form.Item>
        {monthlyPrice != null ? (
          <h3 style={{ textAlign: 'right', margin: 0 }}>
            {`${currencyFormat(monthlyPrice, { currency, maximumFractionDigits: 0 })}/month`}
          </h3>
        ) : (
          <>Not available</>
        )}
      </div>
    </DashboardCard>
  );
};

const getInitialValues = ({ initialModule, activeModuleLicenses, availableModules, activeLicense, isSuperUser }) => ({
  modules: availableModules.map((module) => {
    let priceLevel = 0;
    const existingModule = activeLicense?.interval !== LicenseInterval.Trial && activeLicense?.modules.find(m => m.module === module);
    if (existingModule) {
      const license = getModuleLicense({ activeModuleLicenses, level: activeLicense?.level || 5, module });
      priceLevel = license?.settings?.prices?.findIndex(p => (initialModule === module
        ? p[MODULE_LIMIT_SETTING[module]] > activeLicense.settings[MODULE_LIMIT_SETTING[module]]
        : p[MODULE_LIMIT_SETTING[module]] === activeLicense.settings[MODULE_LIMIT_SETTING[module]]));
    }
    return {
      module,
      priceLevel,
      added: initialModule === module || existingModule || initialModule === true,
    };
  }),
  currency: SEK,
  terms: isSuperUser,
  interval: getValidLicenseInterval({
    activeLicense,
    startDate: dayjs(),
    interval: activeLicense?.interval && activeLicense?.interval !== LicenseInterval.Trial ? activeLicense?.interval : LicenseInterval.Yearly,
  }),
  startDate: dayjs(),
  level: activeLicense?.level || LICENSE_LEVELS.BUSINESS.value,
});

const ModuleLicenseV3Form = ({ form, dataSource, onSubmit, onChange, initialModule = null }) => {
  const $t = useFormatMessage();
  const { isSuperUser } = useUser();
  const { activeLicense } = dataSource;
  const availableModules = dataSourceModules[dataSource.type];
  const { data, loading: licenseLoading } = useQuery(LICENSES, { variables: { levels: [LICENSE_LEVELS.BUSINESS.value, LICENSE_LEVELS.ENTERPRISE.value], modules: availableModules } });
  const startDate = Form.useWatch('startDate', form);
  const interval = Form.useWatch('interval', form);
  const { settings: { bundleDiscounts = [] } = {}, loading: bundleDiscountsLoading } = useSettings({ keys: ['bundleDiscounts'] });

  const loading = licenseLoading || bundleDiscountsLoading;

  const getValues = (values) => {
    const modules = [];
    const settings = [];
    const licenses = [];

    for (const { module, added, priceLevel } of values.modules) {
      const license = getModuleLicense({ activeModuleLicenses: data?.activeModuleLicenses, level: values.level, module });
      if (license && added) {
        const limitSetting = getModuleLimitSetting(module, values.level);
        const limitValue = license.settings?.prices?.[priceLevel]?.[limitSetting];
        if (limitValue) {
          modules.push(license.id);
          settings.push({
            key: limitSetting,
            value: limitValue,
          });
          licenses.push(license);
        }
      }
    }

    return {
      ...values,
      modules,
      settings,
      licenses,
      bundleDiscounts,
    };
  };

  const onFinish = async (values) => {
    onSubmit(getValues(values));
  };

  const initialValues = useMemo(() => getInitialValues({
    initialModule, activeModuleLicenses: data?.activeModuleLicenses, availableModules, activeLicense, isSuperUser,
  }), [initialModule, data?.activeModuleLicenses, availableModules, activeLicense, isSuperUser]);

  const onValuesChange = () => {
    const values = getValues(form.getFieldsValue(true));
    onChange(values);
  };

  useEffect(() => {
    form.resetFields();
    onValuesChange();
  }, [dataSource, data, initialValues]);

  const intervalOptions = [...dataSourceIntervalOptions[dataSource.type]];

  if (isSuperUser) {
    intervalOptions.push(LicenseInterval.Trial);
  }

  const isMidTermChange = startDate && activeLicense?.endDate && dayjs(startDate).isBefore(dayjs(activeLicense.endDate)) && interval !== LicenseInterval.Trial;

  const disabledDate = (date) => {
    if (isSuperUser) {
      return false;
    }
    if (dayjs(date).isBefore(dayjs().startOf('day'))) {
      return true;
    }
    if (activeLicense?.endDate && dayjs(date).isAfter(dayjs(activeLicense.endDate).add(1, 'day'))) {
      return true;
    }
    return false;
  };

  return (
    <Form form={form} onFinish={onFinish} onValuesChange={onValuesChange} layout="vertical" initialValues={initialValues}>
      <Row gutter={[16, 16]}>
        <Col span={4}>
          <Form.Item name="level" label="Plan" rules={[{ required: true, message: 'Required' }]}>
            <Select options={licenseLevelOptions} disabled={!isSuperUser} />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item label="Start date" name="startDate">
            <DatePicker disabledDate={disabledDate} allowClear={false} />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item name="interval" label="Subscription">
            <Select options={intervalOptions.map(o => ({
              value: o,
              label: $t(globalMessages[o.toLowerCase()]),
            }))}
            />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item name="currency" label="Currency">
            <CurrencySelect currencies={currencies} />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item label="Products">
        <Row gutter={16}>
          {availableModules.map((module, index) => (
            <Col span={24 / availableModules.length} key={module}>
              <Form.Item name={['modules', index]} noStyle>
                <ModuleLicenseCard
                  module={module}
                  index={index}
                  activeModuleLicenses={data?.activeModuleLicenses}
                  activeLicense={activeLicense}
                  loading={loading}
                  isMidTermChange={isMidTermChange}
                  isSuperUser={isSuperUser}
                />
              </Form.Item>
            </Col>
          ))}
        </Row>
      </Form.Item>
    </Form>
  );
};

export default ModuleLicenseV3Form;
